import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DropDownList } from 'components/formInputs/dropDownList';
import { code as cmCode } from 'codemash';
import { saveRKeeperCredentials } from 'api/restaurant';
import { FormButton } from 'components/buttons/formButton';
import { Loader } from 'components/indicators/loader';
import { apiActionChooser } from 'utils/apiActionChooser';
import { functionRequest } from 'api/client/requests';

export const RestaurantIntegrationRKeeper = ({ restaurant, integrationSelectionBlock, onSaved, loading, setupData }) => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [productsSyncStarted, setProductsSyncStarted] = useState(false);
  const [tablesSyncStarted, setTablesSyncStarted] = useState(false);
  const [discountsSyncStarted, setDiscountsSyncStarted] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [restaurantOptions, setRestaurantOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [prepaymentcOptions, setPrepaymentOptions] = useState([]);

  const schema = yup.object().shape({
    currency_code: yup.string().default(setupData?.currencyCode),
    restaurant_code: yup.string().default(setupData?.restaurantCode),
    prepayment_reason_code: yup.string().default(setupData?.prepaymentReasonCode),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  const onInitOptions = () => {
    setRestaurantOptions([{ value: '', label: '-' }, ...(setupData?.restaurants || []).map((x) => ({ value: x.code, label: x.name }))]);

    setCurrencyOptions([{ value: '', label: '-' }, ...(setupData?.currencies || []).map((x) => ({ value: x.code, label: x.name }))]);

    setPrepaymentOptions([{ value: '', label: '-' }, ...(setupData?.prepaymentReasons || []).map((x) => ({ value: x.code, label: x.name }))]);
  };

  const resetCredentials = () => {
    if (setupData) {
      reset({
        currency_code: setupData.currencyCode,
        restaurant_code: setupData.restaurantCode,
        prepayment_reason_code: setupData.prepaymentReasonCode,
      });

      onInitOptions();
    } else {
      reset({});

      onInitOptions();
    }
  };

  useEffect(() => {
    resetCredentials();
  }, [setupData, reset]);

  const onSubmit = async (data) => {
    setIsSaving(true);

    const finalCredentials = {
      ...data,
      integrationType: 'rkeeper',
    };

    const response = await saveRKeeperCredentials({
      token: auth.token,
      restaurantId: restaurant._id,
      credentials: finalCredentials,
      alertController,
      companyId: auth.company?.company,
    });

    if (response && response.saved) {
      alertController.success({ text: 'RKeeper integration saved' });

      onSaved('rkeeper', data);
      setIsEditing(false);
    } else if (response && response.saved === false) {
      alertController.error({ text: 'Failed to save RKeeper integration' });
    }

    setIsSaving(false);
  };
  const onError = (data) => console.log(data);

  const onSyncProducts = async () => {
    if (!restaurant?._id) {
      return;
    }

    setProductsSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'products' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'products' }),
          });
        },
      );

      alertController.success({ text: 'Product sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync products' });
    }

    setProductsSyncStarted(false);
  };

  const onSyncTables = async () => {
    if (!restaurant?._id) {
      return;
    }

    setTablesSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'tables' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'tables' }),
          });
        },
      );

      alertController.success({ text: 'Tables sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync products' });
    }

    setTablesSyncStarted(false);
  };

  const onSyncDiscounts = async () => {
    if (!restaurant?._id) {
      return;
    }

    setDiscountsSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'discounts' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'discounts' }),
          });
        },
      );

      alertController.success({ text: 'Discounts sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync discounts' });
    }

    setDiscountsSyncStarted(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      {integrationSelectionBlock}

      {loading && <div className="mt-6"><Loader small /></div>}

      {!loading && (
      <div className="grid grid-cols-6 gap-6 mt-6">
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="restaurant_code" className="block text-sm font-medium text-gray-700">
            Restaurant
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <DropDownList
              {...register('restaurant_code')}
              id="restaurant_code"
              options={restaurantOptions}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              disabled={!isEditing}
            />
          </div>
          {errors?.restaurant_code && (
          <p className="mt-2 text-sm text-red-600" id="restaurant_code-error">
            {errors?.restaurant_code.message}
          </p>
          )}
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="currency_code" className="block text-sm font-medium text-gray-700">
            Currency
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <DropDownList
              {...register('currency_code')}
              id="currency_code"
              options={currencyOptions}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              disabled={!isEditing}
            />
          </div>
          {errors?.currency_code && (
          <p className="mt-2 text-sm text-red-600" id="currency_code-error">
            {errors?.currency_code.message}
          </p>
          )}
        </div>
        <div className="col-span-6 sm:col-span-3">
          <label htmlFor="prepayment_reason_code" className="block text-sm font-medium text-gray-700">
            Prepayment reason
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <DropDownList
              {...register('prepayment_reason_code')}
              id="prepayment_reason_code"
              options={prepaymentcOptions}
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              disabled={!isEditing}
            />
          </div>
          {errors?.prepayment_reason_code && (
          <p className="mt-2 text-sm text-red-600" id="prepayment_reason_code-error">
            {errors?.prepayment_reason_code.message}
          </p>
          )}
        </div>

      </div>
      )}

      {!loading && (
      <div className="mt-4">
        <FormButton
          text={isEditing ? 'Cancel' : 'Edit'}
          noMargin
          outline
          onClick={() => {
            if (isEditing) {
              resetCredentials();
            }

            setIsEditing(!isEditing);
          }}
        />

        {isEditing && (
        <FormButton
          text="Save"
          isLoading={isSaving}
        />
        )}
      </div>
      )}

      {!loading && (
      <fieldset>
        <div className="mt-8">
          <legend className="text-base font-medium text-gray-900">Import data</legend>
          <p className="text-sm text-gray-500">
            Import data from your integration into Waitless
          </p>
        </div>
        <div className="flex flex-row flex-wrap">
          <div className="mr-4 mt-4">
            <FormButton
              text="Import tables"
              isLoading={tablesSyncStarted}
              disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
              onClick={onSyncTables}
              noMargin
            />
          </div>
          <div className="mr-4 mt-4">
            <FormButton
              text="Import products"
              isLoading={productsSyncStarted}
              disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
              onClick={onSyncProducts}
              noMargin
            />
          </div>
          {/* <div className="mr-4 mt-4">
            <FormButton
              text="Import discounts"
              isLoading={discountsSyncStarted}
              disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
              onClick={onSyncDiscounts}
              noMargin
            />
          </div> */}
        </div>
      </fieldset>
      )}
    </form>
  );
};
